/* eslint-disable @typescript-eslint/naming-convention */
import {ClassroomMessage, MessageRecord, RecordStack} from '@app/services/data.service';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {ExecBuffer, LazyExec} from '@app/models/exec-buffer';
import {Store} from '@ngxs/store';

export type CommandMap<E extends string, S extends string> = {
  EVENT: {[key in E]: (v: MessageRecord | RecordStack) => void};
  STATE: {[key in S]: (v: MessageRecord | RecordStack) => void};
};

export class CommandsDescriptor<E extends string, S extends string> {
  protected localUser: LocalUser;
  protected buffer: ExecBuffer;
  public MAP: CommandMap<E, S>;

  constructor(map: CommandMap<E, S>, buffer: ExecBuffer, private store: Store) {
    this.MAP = map;
    this.buffer = buffer;
    this.listenStoreChanges();
  }

  public handleMessage(message: ClassroomMessage): void {
    if (!message.selfProcess && message.record.ownerId === this.localUser.id) {
      return;
    }
    this.handleRecord(message.record);
  }

  public async handleRecordStack(recordStack: RecordStack): Promise<void> {
    await recordStack.events
      .reduce((previousPromise: Promise<any>, messageRecord: MessageRecord) =>
          previousPromise.then((): Promise<any> =>
            this.messageRecordToPromise(messageRecord)
          ),
        Promise.resolve()
      );

    await recordStack.states
      .sort((a, b) => {
        return a.timestamp - b.timestamp;   // sort by increasing
      })
      .reduce((previousPromise: Promise<any>, messageRecord: MessageRecord) =>
          previousPromise.then((): Promise<any> =>
            this.messageRecordToPromise(messageRecord)
          ),
        Promise.resolve()
      );

    const timestamps: number[] = [...recordStack.events, ...recordStack.states]
      .map((record: MessageRecord) => record.timestamp);

    const maxTimestamp: number = Math.max(...timestamps);

    this.buffer.normalizeAndExecQueue(maxTimestamp);
  }

  protected handleRecord(record: MessageRecord, nonQueue = false): void | Promise<any> {
    const func = this.MAP[record.type][record.name];
    if (func) {
      if (nonQueue) {
        return func(record);   // apply out of queue (record stack case)
      } else {
        this.buffer.addToQueue(new LazyExec(func, record));
      }
    }
  }

  protected messageRecordToPromise(messageRecord: MessageRecord): Promise<any> {
    const result: void | Promise<any> = this.handleRecord(messageRecord, true);
    if (result instanceof Promise) {
      return result;
    } else {
      return Promise.resolve();
    }
  }

  private listenStoreChanges(): void {
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
  }
}
